import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Autocomplete, TextField } from "@mui/material";
import { AiFillAppstore } from "react-icons/ai";
import { FiCalendar, FiFileText, FiPrinter, FiRefreshCw } from "react-icons/fi";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { errorAlert } from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import "../Moduli_Prodhimit/Prodhimi_Standard/Prodhimi_Standard/StandardProducePage.scss";

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-GB").replace(/\//g, ".");
};

const shortenDateLabel = (label) => {
  if (!label) {
    return "";
  }

  const parts = String(label).split(".");
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : label;
};

const chartColors = ["#284945", "#b76e2b", "#3566a8", "#6b8f3a", "#8f4c6a", "#6f5aa8", "#2f7f83", "#a74737"];

const qtyFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatQty = (value) => qtyFormatter.format(Number(value || 0));
const chartDetailPageSize = 50;
const maxChartArticles = 6;

const hexToRgb = (hex) => {
  const normalized = String(hex || "").replace("#", "");
  const value = normalized.length === 3
    ? normalized.split("").map((char) => `${char}${char}`).join("")
    : normalized;
  const parsed = Number.parseInt(value, 16);

  if (Number.isNaN(parsed)) {
    return [40, 73, 69];
  }

  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255];
};

const toInputDate = (date) => {
  const dt = new Date(date);
  if (Number.isNaN(dt.getTime())) {
    return "";
  }

  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentDayRange = () => {
  const today = toInputDate(new Date());

  return {
    firstDate: today,
    secondDate: today,
  };
};

const readField = (obj, keys, fallback = null) => {
  if (!obj) {
    return fallback;
  }

  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return obj[key];
    }
  }

  return fallback;
};

function ProductionChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  const visibleItems = payload.filter((item) => Number(item.value || 0) > 0);
  const tooltipTitle = payload?.[0]?.payload?.dateLabel || label;

  return (
    <div className="SPP__chartTooltip">
      <div className="SPP__chartTooltipTitle">{tooltipTitle}</div>
      {visibleItems.map((item) => (
        <div key={item.dataKey} className="SPP__chartTooltipRow">
          <span className="SPP__chartTooltipDot" style={{ background: item.color }} />
          <span>{item.name}</span>
          <strong>{Number(item.value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
        </div>
      ))}
    </div>
  );
}

function ProductionChartLegend({ payload }) {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="SPP__chartLegend">
      {payload.map((entry, index) => (
        <span key={entry.value} className="SPP__chartLegendPill">
          <span className="SPP__chartLegendNumber">{index + 1}.</span>
          <span className="SPP__chartLegendLine" style={{ background: entry.color }} />
          <span className="SPP__chartLegendText">{entry.value}</span>
        </span>
      ))}
    </div>
  );
}

function ProductionBarIndexLabel({ x, y, width, value, articleIndex, color }) {
  if (!value) {
    return null;
  }

  const labelX = x + width / 2;
  const labelY = Math.max(10, y - 7);

  return (
    <text
      x={labelX}
      y={labelY}
      textAnchor="middle"
      dominantBaseline="middle"
      fill={color}
      fontSize={10}
      fontWeight={900}
      paintOrder="stroke"
      stroke="#ffffff"
      strokeWidth={3}
    >
      {articleIndex + 1}
    </text>
  );
}

export default function ProductionReportPage() {
  const headers = authHeader();
  const defaultDay = useMemo(() => getCurrentDayRange(), []);
  const chartBodyRef = useRef(null);

  const [chartLoading, setChartLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartDetailData, setChartDetailData] = useState([]);
  const [chartDetailTotal, setChartDetailTotal] = useState(0);
  const [chartDetailPage, setChartDetailPage] = useState(0);
  const [articleOptions, setArticleOptions] = useState([]);
  const [selectedChartArticle, setSelectedChartArticle] = useState(null);
  const [chartFirstDate, setChartFirstDate] = useState(defaultDay.firstDate);
  const [chartSecondDate, setChartSecondDate] = useState(defaultDay.secondDate);
  const [showChartDetails, setShowChartDetails] = useState(false);

  const fetchChartArticles = async () => {
    try {
      const response = await axios.get(`${ApiLink.API_URL}CompoundArticle`, { headers });
      setArticleOptions(Array.isArray(response?.data) ? response.data : []);
    } catch {
      setArticleOptions([]);
    }
  };

  const fetchProductionChart = async () => {
    if (!chartFirstDate || !chartSecondDate) {
      setChartData([]);
      setChartDetailData([]);
      setChartDetailTotal(0);
      return;
    }

    setChartLoading(true);
    try {
      const response = await axios.get(`${ApiLink.API_URL}StandardProduce/productionChart`, {
        headers,
        params: {
          firstDate: chartFirstDate,
          secondDate: chartSecondDate,
          articleID: selectedChartArticle?.ArticleID || undefined,
          detailPage: chartDetailPage,
          detailPageSize: chartDetailPageSize,
        },
      });

      const items = response?.data?.Items || response?.data?.items || [];
      const detailItems = response?.data?.DetailItems || response?.data?.detailItems || [];
      setChartData(Array.isArray(items) ? items : []);
      setChartDetailData(Array.isArray(detailItems) ? detailItems : []);
      setChartDetailTotal(Number(response?.data?.DetailTotal || response?.data?.detailTotal || 0));
    } catch (error) {
      setChartData([]);
      setChartDetailData([]);
      setChartDetailTotal(0);
      errorAlert(
        error?.response?.data?.message ||
          error?.response?.data?.errorMessage ||
          error?.message ||
          "Gabim në marrjen e të dhënave për chart."
      );
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    fetchChartArticles();
  }, []);

  useEffect(() => {
    fetchProductionChart();
  }, [chartFirstDate, chartSecondDate, selectedChartArticle, chartDetailPage]);

  const chartArticles = useMemo(() => {
    const articlesMap = new Map();
    chartData.forEach((item) => {
      const articleID = Number(readField(item, ["ArticleID", "articleID"], 0));
      if (!articleID) {
        return;
      }

      const current = articlesMap.get(articleID) || {
        articleID,
        articleName: readField(item, ["ArticleName", "articleName"], "-"),
        totalQty: 0,
      };

      current.totalQty += Number(readField(item, ["Qty", "qty"], 0));
      articlesMap.set(articleID, current);
    });

    return Array.from(articlesMap.values())
      .sort((first, second) => second.totalQty - first.totalQty)
      .slice(0, maxChartArticles);
  }, [chartData]);

  const visibleChartArticleIds = useMemo(
    () => new Set(chartArticles.map((article) => article.articleID)),
    [chartArticles]
  );

  const chartRows = useMemo(() => {
    const rowsMap = new Map();

    chartData.forEach((item) => {
      const dateLabel = readField(item, ["DateLabel", "dateLabel"], formatDate(readField(item, ["Date", "date"], "")));
      const articleID = Number(readField(item, ["ArticleID", "articleID"], 0));
      const qty = Number(readField(item, ["Qty", "qty"], 0));
      const key = `article_${articleID}`;

      if (!rowsMap.has(dateLabel)) {
        rowsMap.set(dateLabel, { dateLabel, axisLabel: shortenDateLabel(dateLabel), details: [] });
      }

      const row = rowsMap.get(dateLabel);
      if (visibleChartArticleIds.has(articleID) && qty > 0) {
        row[key] = Number(row[key] || 0) + qty;
      }

      row.details.push({
        articleID,
        articleName: readField(item, ["ArticleName", "articleName"], "-"),
        qty,
        unitName: readField(item, ["UnitName", "unitName"], ""),
      });
    });

    return Array.from(rowsMap.values());
  }, [chartData, visibleChartArticleIds]);

  const articleColorMap = useMemo(() => {
    const colors = {};
    chartArticles.forEach((article, index) => {
      colors[article.articleID] = chartColors[index % chartColors.length];
    });
    return colors;
  }, [chartArticles]);

  const detailRows = useMemo(
    () => chartDetailData.map((item, index) => ({
      id: `${readField(item, ["DateLabel", "dateLabel"], "date")}-${readField(item, ["ArticleID", "articleID"], "article")}-${index}`,
      articleID: Number(readField(item, ["ArticleID", "articleID"], 0)),
      dateLabel: readField(item, ["DateLabel", "dateLabel"], formatDate(readField(item, ["Date", "date"], ""))),
      articleName: readField(item, ["ArticleName", "articleName"], "-"),
      barcode: readField(item, ["Barcode", "barcode"], "-"),
      unitName: readField(item, ["UnitName", "unitName"], ""),
      qty: Number(readField(item, ["Qty", "qty"], 0)),
    })),
    [chartDetailData]
  );

  const chartDetailTotalPages = Math.max(1, Math.ceil(chartDetailTotal / chartDetailPageSize));
  const chartDetailFrom = chartDetailTotal === 0 ? 0 : chartDetailPage * chartDetailPageSize + 1;
  const chartDetailTo = Math.min(chartDetailTotal, (chartDetailPage + 1) * chartDetailPageSize);
  const xAxisShouldAngle = chartRows.length > 10;
  const xAxisInterval = chartRows.length > 28 ? Math.ceil(chartRows.length / 18) : 0;

  const getChartImageForPdf = () =>
    new Promise((resolve) => {
      const svg = chartBodyRef.current?.querySelector("svg");
      if (!svg) {
        resolve(null);
        return;
      }

      const clonedSvg = svg.cloneNode(true);
      clonedSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

      const svgText = new XMLSerializer().serializeToString(clonedSvg);
      const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
      const svgUrl = URL.createObjectURL(svgBlob);
      const image = new Image();

      image.onload = () => {
        const scale = 2;
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, image.width * scale);
        canvas.height = Math.max(1, image.height * scale);

        const context = canvas.getContext("2d");
        context.fillStyle = "#ffffff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        URL.revokeObjectURL(svgUrl);
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: image.width,
          height: image.height,
        });
      };

      image.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        resolve(null);
      };

      image.src = svgUrl;
    });

  const buildProductionChartPdf = async () => {
    if (!chartData.length) {
      errorAlert("Nuk ka të dhëna për printim ose PDF.");
      return null;
    }

    const chartImage = await getChartImageForPdf();
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const periodLabel = `${formatDate(chartFirstDate)} - ${formatDate(chartSecondDate)}`;
    const articleLabel = selectedChartArticle?.ArticleName || "Të gjithë artikujt";
    const generatedDate = formatDate(new Date());
    const totalQty = chartData.reduce((sum, row) => sum + Number(readField(row, ["Qty", "qty"], 0)), 0);
    doc.setFillColor(40, 73, 69);
    doc.rect(0, 0, pageWidth, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Prodhimi ditor i artikujve", 14, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Gjeneruar: ${generatedDate}`, pageWidth - 14, 13, { align: "right" });

    doc.setTextColor(17, 38, 58);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Periudha", 14, 40);
    doc.text("Artikulli", 86, 40);
    doc.text("Sasia totale", 174, 40);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(periodLabel, 14, 47);
    doc.text(articleLabel, 86, 47);
    doc.text(formatQty(totalQty), 174, 47);

    if (chartImage?.dataUrl) {
      const chartWidth = pageWidth - 28;
      const chartHeight = Math.min(116, (chartImage.height / chartImage.width) * chartWidth);
      const legendStartY = 55 + chartHeight + 18;
      const legendColumnWidth = (chartWidth - 12) / 2;
      doc.setDrawColor(216, 232, 225);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(14, 55, chartWidth, chartHeight + 10, 2, 2, "FD");
      doc.addImage(chartImage.dataUrl, "PNG", 18, 59, chartWidth - 8, chartHeight);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(17, 38, 58);
      doc.text("Legjenda e artikujve", 14, legendStartY);

      chartArticles.forEach((article, index) => {
        const color = chartColors[index % chartColors.length];
        const [red, green, blue] = hexToRgb(color);
        const column = index % 2;
        const row = Math.floor(index / 2);
        const itemX = 14 + column * (legendColumnWidth + 12);
        const itemY = legendStartY + 9 + row * 10;

        doc.setTextColor(17, 38, 58);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.text(`${index + 1}.`, itemX, itemY);

        doc.setFillColor(red, green, blue);
        doc.roundedRect(itemX + 8, itemY - 3.8, 9, 3, 1, 1, "F");
        doc.setTextColor(28, 53, 50);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.text(article.articleName, itemX + 20, itemY, { maxWidth: legendColumnWidth - 22 });
      });
    }

    doc.setFontSize(8);
    doc.setTextColor(93, 115, 133);
    doc.text("Raport grafik i prodhimit. Tabela eksportohet nga seksioni i datagrid-it.", 14, doc.internal.pageSize.getHeight() - 8);
    doc.text("Faqe 1", pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });

    return doc;
  };

  const printPdfDocument = (doc) => {
    const pdfBlob = doc.output("blob");
    const printUrl = URL.createObjectURL(pdfBlob);
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.left = "-10000px";
    printFrame.style.top = "0";
    printFrame.style.width = "1024px";
    printFrame.style.height = "768px";
    printFrame.style.border = "0";
    printFrame.style.opacity = "0";
    printFrame.style.pointerEvents = "none";
    let didPrint = false;
    let cleanupDone = false;

    const cleanup = () => {
      if (cleanupDone) {
        return;
      }

      cleanupDone = true;
      URL.revokeObjectURL(printUrl);
      if (printFrame.parentNode) {
        printFrame.parentNode.removeChild(printFrame);
      }
    };

    printFrame.onload = () => {
      if (didPrint) {
        return;
      }

      didPrint = true;

      try {
        const frameWindow = printFrame.contentWindow;
        if (!frameWindow) {
          cleanup();
          errorAlert("Printimi nuk u inicializua. Ju lutem provoni përsëri.");
          return;
        }

        window.setTimeout(() => {
          frameWindow.focus();
          frameWindow.onafterprint = cleanup;
          frameWindow.print();
          window.setTimeout(cleanup, 5000);
        }, 450);
      } catch {
        cleanup();
        errorAlert("Nuk u hap dialogu i printimit. Ju lutem provoni përsëri.");
      }
    };

    document.body.appendChild(printFrame);
    printFrame.src = printUrl;

    window.setTimeout(cleanup, 60_000);
  };

  const handleExportChartPdf = async () => {
    const doc = await buildProductionChartPdf();
    if (!doc) {
      return;
    }

    const filePeriod = `${chartFirstDate || "nga"}_${chartSecondDate || "deri"}`;
    doc.save(`Prodhimi_ditor_${filePeriod}.pdf`);
  };

  const handlePrintChart = async () => {
    const doc = await buildProductionChartPdf();
    if (!doc) {
      return;
    }

    printPdfDocument(doc);
  };

  const buildProductionDetailsPdf = () => {
    if (!detailRows.length) {
      errorAlert("Nuk ka të dhëna në tabelë për printim ose PDF.");
      return null;
    }

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const periodLabel = `${formatDate(chartFirstDate)} - ${formatDate(chartSecondDate)}`;
    const articleLabel = selectedChartArticle?.ArticleName || "Të gjithë artikujt";
    const generatedDate = formatDate(new Date());

    doc.setFillColor(40, 73, 69);
    doc.rect(0, 0, pageWidth, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Detajet e prodhimit", 14, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Gjeneruar: ${generatedDate}`, pageWidth - 14, 13, { align: "right" });

    doc.setTextColor(17, 38, 58);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Periudha", 14, 40);
    doc.text("Artikulli", 86, 40);
    doc.text("Rreshtat", 174, 40);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(periodLabel, 14, 47);
    doc.text(articleLabel, 86, 47, { maxWidth: 78 });
    doc.text(`${chartDetailFrom}-${chartDetailTo} nga ${chartDetailTotal}`, 174, 47);

    doc.autoTable({
      startY: 58,
      head: [["Data", "Artikulli", "Barkodi", "Sasia", "Njësia"]],
      body: detailRows.map((row) => [
        row.dateLabel,
        row.articleName,
        row.barcode,
        formatQty(row.qty),
        row.unitName || "-",
      ]),
      styles: {
        font: "helvetica",
        fontSize: 8.5,
        cellPadding: 2.8,
        textColor: [17, 38, 58],
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [248, 252, 250],
      },
      columnStyles: {
        3: { halign: "right" },
      },
      margin: { left: 14, right: 14 },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        doc.setTextColor(93, 115, 133);
        doc.text("Raport tabelar i prodhimit. Chart-i eksportohet nga butonat e chart-it.", 14, doc.internal.pageSize.getHeight() - 8);
        doc.text(`Faqe ${data.pageNumber}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
      },
    });

    return doc;
  };

  const handleExportDetailsPdf = () => {
    const doc = buildProductionDetailsPdf();
    if (!doc) {
      return;
    }

    const filePeriod = `${chartFirstDate || "nga"}_${chartSecondDate || "deri"}`;
    doc.save(`Detajet_e_prodhimit_${filePeriod}_faqja_${chartDetailPage + 1}.pdf`);
  };

  const handlePrintDetails = () => {
    const doc = buildProductionDetailsPdf();
    if (!doc) {
      return;
    }

    printPdfDocument(doc);
  };

  return (
    <div className="Crud__Container SPP__page">
      <div className="SPP__content">
        <CrudPageHeader
          icon={AiFillAppstore}
          title="Raporti i prodhimit"
          description="Analizë grafike dhe tabelare e prodhimit të artikujve sipas periudhës."
          tutorialUrl="https://www.youtube.com/@Accountree"
          tutorialLabel="Shiko Tutorialin"
          className="SPP__header"
        />

        <section className="SPP__chartSection">
          <div className="SPP__chartTopbar">
            <div className="SPP__chartTitleBlock">
              <span className="SPP__chartEyebrow">Analiza e prodhimit</span>
              <h3>Prodhimi ditor i artikujve</h3>
            </div>

            <div className="SPP__chartActions">
              <button
                type="button"
                className="SPP__chartActionBtn SPP__chartActionBtn--details"
                onClick={() => setShowChartDetails((prev) => !prev)}
                disabled={chartLoading || chartDetailTotal === 0}
              >
                {showChartDetails ? "FSHEH TABELËN" : "SHFAQ TABELËN"}
              </button>
              <button
                type="button"
                className="SPP__chartActionBtn"
                onClick={handlePrintChart}
                disabled={chartLoading || chartData.length === 0}
              >
                <FiPrinter size={15} />
                PRINT
              </button>
              <button
                type="button"
                className="SPP__chartActionBtn SPP__chartActionBtn--pdf"
                onClick={handleExportChartPdf}
                disabled={chartLoading || chartData.length === 0}
              >
                <FiFileText size={15} />
                PDF
              </button>
            </div>
          </div>

          <div className="SPP__chartFilters">
            <div className="SPP__chartFilterGroup SPP__chartFilterGroup--dates">
              <div className="SPP__chartFilterLabel">
                <FiCalendar size={14} />
                Periudha
              </div>
              <div className="SPP__dateRangeFields">
                <TextField
                  label="Nga"
                  type="date"
                  size="small"
                  value={chartFirstDate}
                  onChange={(event) => {
                    setChartDetailPage(0);
                    setChartFirstDate(event.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Deri"
                  type="date"
                  size="small"
                  value={chartSecondDate}
                  onChange={(event) => {
                    setChartDetailPage(0);
                    setChartSecondDate(event.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>

            <div className="SPP__chartFilterGroup SPP__chartFilterGroup--article">
              <div className="SPP__chartFilterLabel">Artikulli</div>
              <Autocomplete
                className="SPP__chartArticleFilter"
                size="small"
                options={articleOptions}
                value={selectedChartArticle}
                onChange={(_, value) => {
                  setChartDetailPage(0);
                  setSelectedChartArticle(value);
                }}
                getOptionLabel={(option) => option?.ArticleName || ""}
                isOptionEqualToValue={(option, value) => Number(option?.ArticleID) === Number(value?.ArticleID)}
                renderInput={(params) => <TextField {...params} placeholder="Të gjithë artikujt" />}
              />
            </div>

            <button
              type="button"
              className="SPP__chartResetBtn"
              onClick={() => {
                setChartDetailPage(0);
                setChartFirstDate(defaultDay.firstDate);
                setChartSecondDate(defaultDay.secondDate);
                setSelectedChartArticle(null);
              }}
            >
              <FiRefreshCw size={14} />
              Dita aktuale
            </button>
          </div>

          <div className="SPP__chartBody" ref={chartBodyRef}>
            {chartLoading ? (
              <div className="SPP__chartEmpty">Duke ngarkuar chart-in...</div>
            ) : chartRows.length === 0 ? (
              <div className="SPP__chartEmpty">Nuk ka prodhime për periudhën e zgjedhur.</div>
            ) : (
              <ResponsiveContainer width="100%" height={390}>
                <BarChart data={chartRows} margin={{ top: 18, right: 26, left: -8, bottom: xAxisShouldAngle ? 42 : 16 }} barCategoryGap="18%" barGap={3}>
                  <CartesianGrid strokeDasharray="5 5" vertical={false} stroke="#d9e8e2" />
                  <XAxis
                    dataKey="axisLabel"
                    axisLine={{ stroke: "#536b61" }}
                    tickLine={false}
                    interval={xAxisInterval}
                    angle={xAxisShouldAngle ? -35 : 0}
                    textAnchor={xAxisShouldAngle ? "end" : "middle"}
                    height={xAxisShouldAngle ? 60 : 34}
                    tick={{ fill: "#425d57", fontSize: 12, fontWeight: 700 }}
                  />
                  <YAxis axisLine={{ stroke: "#536b61" }} tickLine={false} tick={{ fill: "#425d57", fontSize: 12, fontWeight: 700 }} />
                  <Tooltip content={<ProductionChartTooltip />} />
                  <Legend content={<ProductionChartLegend />} />
                  {chartArticles.map((article, index) => (
                    <Bar
                      key={article.articleID}
                      dataKey={`article_${article.articleID}`}
                      name={article.articleName}
                      fill={chartColors[index % chartColors.length]}
                      stroke={chartColors[index % chartColors.length]}
                      strokeWidth={1.5}
                      radius={[6, 6, 0, 0]}
                      maxBarSize={56}
                    >
                      <LabelList
                        content={(props) => (
                          <ProductionBarIndexLabel
                            {...props}
                            articleIndex={index}
                            color={chartColors[index % chartColors.length]}
                          />
                        )}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {showChartDetails && !chartLoading && detailRows.length > 0 && (
            <div className="SPP__chartDetails">
              <div className="SPP__chartDetailsHead">
                <div>
                  <strong>Detajet e prodhimit</strong>
                  <span>{chartDetailTotal} rreshta</span>
                </div>
                <div className="SPP__chartDetailsActions">
                  <button
                    type="button"
                    className="SPP__chartActionBtn"
                    onClick={handlePrintDetails}
                    disabled={chartLoading || detailRows.length === 0}
                  >
                    <FiPrinter size={15} />
                    PRINT
                  </button>
                  <button
                    type="button"
                    className="SPP__chartActionBtn SPP__chartActionBtn--pdf"
                    onClick={handleExportDetailsPdf}
                    disabled={chartLoading || detailRows.length === 0}
                  >
                    <FiFileText size={15} />
                    PDF
                  </button>
                </div>
              </div>
              <div className="SPP__chartTableWrap">
                <table className="SPP__chartTable">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Artikulli</th>
                      <th>Barkodi</th>
                      <th className="SPP__numericCell">Sasia</th>
                      <th>Njësia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.dateLabel}</td>
                        <td>
                          <span className="SPP__articleNameCell">
                            <span className="SPP__articleColorMark" style={{ background: articleColorMap[row.articleID] || "#284945" }} />
                            {row.articleName}
                          </span>
                        </td>
                        <td>{row.barcode}</td>
                        <td className="SPP__numericCell">{formatQty(row.qty)}</td>
                        <td>{row.unitName || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="SPP__chartPagination">
                <span>
                  {chartDetailFrom}-{chartDetailTo} nga {chartDetailTotal}
                </span>
                <div className="SPP__chartPaginationControls">
                  <button
                    type="button"
                    onClick={() => setChartDetailPage((page) => Math.max(0, page - 1))}
                    disabled={chartDetailPage === 0}
                  >
                    Para
                  </button>
                  <strong>
                    Faqja {chartDetailPage + 1} / {chartDetailTotalPages}
                  </strong>
                  <button
                    type="button"
                    onClick={() => setChartDetailPage((page) => Math.min(chartDetailTotalPages - 1, page + 1))}
                    disabled={chartDetailPage >= chartDetailTotalPages - 1}
                  >
                    Pas
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
